import React, { useState, useRef, useEffect } from 'react'
import { Heart, ExternalLink, MapPin, CheckCircle } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

// Compact, forgiving relative timestamp (e.g. "just now", "3h", "2d", "Mar 4").
// Returns null for missing/invalid input so the header degrades gracefully.
function relativeTime(iso?: string | null): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const diff = Date.now() - then
  if (diff < 0) return 'just now'
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day}d`
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Author row shown at the top of every feed card. Creator/admin posts are
// brand-attributed ("Mini" + verified tick). Fan-submitted posts arrive from
// the API with an anonymized `author_label` ("fan xxxxx") — those render the
// label with NO verified tick (the tick is reserved for the creator).
function CardHeader({ block }: BlockProps) {
  const time = relativeTime(block.created_at)
  const isFanPost = !!block.author_label
  const authorName = block.author_label || 'Mini'
  const avatarInitial = authorName.charAt(0).toUpperCase() || 'M'
  return (
    <div className="flex items-center gap-3 px-4 pt-4">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#87CEEB] to-[#4AA3CC] flex items-center justify-center text-white font-semibold text-sm shadow-sm flex-shrink-0">
        {avatarInitial}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="font-semibold text-gray-900 text-sm leading-tight">{authorName}</span>
          {!isFanPost && <CheckCircle size={13} className="text-[#4AA3CC] flex-shrink-0" />}
        </div>
        <div className="text-xs text-gray-500 leading-tight">
          {time ? time : "Mini's World"}
        </div>
      </div>
    </div>
  )
}

interface BlockProps {
  block: any
}

export function NewsroomBlock({ block }: BlockProps) {
  const [likes, setLikes] = useState(block.like_count || 0)
  const [isLiked, setIsLiked] = useState(block.user_liked || false)
  const [isLiking, setIsLiking] = useState(false)
  const [showHeartPop, setShowHeartPop] = useState(false)

  // Handle like - unlimited likes per user with heart animation
  const handleLike = async () => {
    const token = localStorage.getItem('auth_token')
    if (!token) return // Silently fail - newsroom requires login anyway

    try {
      setIsLiking(true)

      // Optimistic update - increment immediately for instant feedback
      setLikes((prev: number) => prev + 1)
      setIsLiked(true)

      // Trigger heart pop animation
      setShowHeartPop(true)
      setTimeout(() => setShowHeartPop(false), 300)

      const response = await fetch(`${API_URL}/api/newsroom/blocks/${block.id}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        // Revert on error
        setLikes((prev: number) => prev - 1)
      }
    } catch (error) {
      // Revert on error
      setLikes((prev: number) => prev - 1)
      console.error('Like error:', error)
    } finally {
      setIsLiking(false)
    }
  }

  // Get grid column span based on new layout config (12-column grid)
  const getGridSpanClass = () => {
    const gridSpan = block.layout_config?.gridSpan || 12

    // Map gridSpan to Tailwind classes
    const spanClasses: Record<number, string> = {
      1: 'md:col-span-1',
      2: 'md:col-span-2',
      3: 'md:col-span-3',
      4: 'md:col-span-4',
      5: 'md:col-span-5',
      6: 'md:col-span-6',
      7: 'md:col-span-7',
      8: 'md:col-span-8',
      9: 'md:col-span-9',
      10: 'md:col-span-10',
      11: 'md:col-span-11',
      12: 'md:col-span-12',
    }

    // Mobile always full width (col-span-12)
    return `col-span-12 ${spanClasses[gridSpan] || 'md:col-span-12'}`
  }

  // Get custom height style
  const getHeightStyle = () => {
    const customHeight = block.layout_config?.customHeight
    if (customHeight) {
      return { height: `${customHeight}px`, minHeight: '120px' }
    }
    return { minHeight: '120px' }
  }

  return (
    <div className={getGridSpanClass()} style={getHeightStyle()}>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <CardHeader block={block} />
        <div className="p-4 flex-1 min-h-0">
          {/* Render block based on type */}
          {block.block_type === 'text' && <TextBlock block={block} />}
          {block.block_type === 'voice' && <VoiceBlock block={block} />}
          {block.block_type === 'video' && <VideoBlock block={block} />}
          {block.block_type === 'photo' && <PhotoBlock block={block} />}
          {block.block_type === 'gallery' && <GalleryBlock block={block} />}
          {block.block_type === 'qna' && <QnABlock block={block} />}
          {block.block_type === 'link' && <LinkBlock block={block} />}
          {block.block_type === 'map_pin' && <MapPinBlock block={block} />}
        </div>

        {/* Like button with heart pop animation */}
        <div className="px-4 pb-3">
          <button
            onClick={handleLike}
            disabled={isLiking}
            className="flex items-center gap-1.5 hover:scale-105 active:scale-95 transition-transform"
          >
            <Heart
              size={18}
              className={`
                ${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-400'}
                transition-all duration-150 ease-out
                ${showHeartPop ? 'scale-150' : 'scale-100'}
              `}
            />
            <span className="text-sm text-gray-500">{likes}</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// Text Block - Responsive typography with proper line height
function TextBlock({ block }: BlockProps) {
  return (
    <div className="prose prose-sm sm:prose max-w-none">
      <p className="text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
        {block.content.text}
      </p>
    </div>
  )
}

// Voice Block with Waveform
function VoiceBlock({ block }: BlockProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [waveformBars, setWaveformBars] = useState<number[]>([])
  const audioRef = useRef<HTMLAudioElement>(null)

  // Generate waveform from audio file using Web Audio API
  useEffect(() => {
    if (block.file_url) {
      analyzeAudio(block.file_url)
    }
  }, [block.file_url])

  const analyzeAudio = async (url: string) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const response = await fetch(url)
      const arrayBuffer = await response.arrayBuffer()
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

      const rawData = audioBuffer.getChannelData(0) // Get first channel
      const samples = 50 // Number of bars to display
      const blockSize = Math.floor(rawData.length / samples)
      const bars: number[] = []

      // Calculate average amplitude for each bar
      for (let i = 0; i < samples; i++) {
        let sum = 0
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[i * blockSize + j])
        }
        // Normalize and amplify for better visual effect
        bars.push((sum / blockSize) * 2)
      }

      // Normalize to 0-1 range
      const maxBar = Math.max(...bars)
      const normalizedBars = bars.map(bar => Math.min(1, bar / maxBar))

      setWaveformBars(normalizedBars)
    } catch (error) {
      console.error('Failed to analyze audio:', error)
      // Fallback to fake data if analysis fails (CORS issues, etc.)
      setWaveformBars(
        Array.from({ length: 50 }, (_, i) =>
          Math.sin(i * 0.5) * 0.5 + 0.5 + Math.random() * 0.3
        )
      )
    }
  }

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left
      const percentage = x / rect.width
      audioRef.current.currentTime = percentage * duration
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!block.file_url) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
        <div className="text-2xl">🎤</div>
        <div>
          <p className="text-sm font-medium text-gray-700">Voice Note</p>
          <p className="text-xs text-gray-500">No audio uploaded</p>
        </div>
      </div>
    )
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="bg-gradient-to-br from-sky-50 to-[#F0F8FF] rounded-lg p-3 sm:p-4">
      <audio
        ref={audioRef}
        src={block.file_url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Play/Pause Button - Larger touch target for mobile */}
        <button
          onClick={togglePlay}
          className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-sky-600 hover:bg-sky-700 active:scale-95 text-white flex items-center justify-center transition-all shadow-md touch-manipulation"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" rx="1"/>
              <rect x="14" y="4" width="4" height="16" rx="1"/>
            </svg>
          ) : (
            <svg className="w-5 h-5 sm:w-6 sm:h-6 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </button>

        {/* Waveform Visualization - Responsive */}
        <div className="flex-1 min-w-0">
          <div
            className="relative h-10 sm:h-12 flex items-center gap-0.5 cursor-pointer touch-manipulation"
            onClick={handleSeek}
            role="slider"
            aria-label="Audio progress"
            aria-valuemin={0}
            aria-valuemax={duration}
            aria-valuenow={currentTime}
          >
            {waveformBars.map((height, i) => {
              const barProgress = (i / waveformBars.length) * 100
              const isPassed = barProgress <= progress

              return (
                <div
                  key={i}
                  className={`flex-1 rounded-full transition-all duration-150 ${
                    isPassed
                      ? 'bg-sky-600'
                      : 'bg-sky-200'
                  } ${isPlaying && isPassed ? 'animate-pulse' : ''}`}
                  style={{
                    height: `${height * 100}%`,
                    minWidth: '2px',
                  }}
                />
              )
            })}
          </div>

          {/* Time Display - Responsive font size */}
          <div className="flex justify-between items-center mt-1 sm:mt-1.5">
            <span className="text-xs sm:text-sm text-gray-600 font-medium tabular-nums">
              {formatTime(currentTime)}
            </span>
            <span className="text-xs sm:text-sm text-gray-600 font-medium tabular-nums">
              {duration > 0 ? formatTime(duration) : block.content.duration || '0:00'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// Video Block - Flexible height respecting original aspect ratio
function VideoBlock({ block }: BlockProps) {
  return (
    <div className="space-y-1">
      {block.content.title && (
        <h3 className="text-sm sm:text-base font-semibold text-gray-900 leading-tight">
          {block.content.title}
        </h3>
      )}
      {block.file_url ? (
        <div className="relative rounded-lg overflow-hidden bg-black shadow-sm max-h-[600px] flex items-center justify-center">
          <video
            controls
            className="w-full max-h-[600px]"
            preload="metadata"
          >
            <source src={block.file_url} type="video/mp4" />
            Your browser does not support video playback.
          </video>
        </div>
      ) : (
        <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
          <div className="text-center">
            <div className="text-4xl mb-2">🎥</div>
            <span className="text-sm text-gray-500">Video not available</span>
          </div>
        </div>
      )}
    </div>
  )
}

// Photo Block - Flexible height respecting original aspect ratio
function PhotoBlock({ block }: BlockProps) {
  return block.file_url ? (
    <div className="relative w-full h-full flex items-center justify-center rounded-lg overflow-hidden shadow-sm">
      <img
        src={block.file_url}
        alt=""
        className="max-w-full max-h-full object-contain"
        loading="lazy"
      />
    </div>
  ) : (
    <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
      <div className="text-center">
        <div className="text-4xl mb-2">📷</div>
        <span className="text-sm text-gray-500">Image not available</span>
      </div>
    </div>
  )
}

// Gallery Block - Grid layout with configurable columns per row
function GalleryBlock({ block }: BlockProps) {
  const [currentPage, setCurrentPage] = useState(0) // For desktop grid pagination
  const [mobileIndex, setMobileIndex] = useState(0) // For mobile carousel
  const images = block.file_url
    ? [block.file_url, ...(block.content.images || [])]
    : block.content.images || []

  if (images.length === 0) {
    return (
      <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
        <span className="text-gray-500">Gallery not available</span>
      </div>
    )
  }

  // Get images per row from config (default: 3)
  const imagesPerRow = block.content?.imagesPerRow || 3

  // Calculate total pages based on desktop layout
  const totalPages = Math.ceil(images.length / imagesPerRow)

  // Reset currentPage if it's out of bounds
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(0)
    }
  }, [totalPages, currentPage])

  // Get images for current page (desktop)
  const startIndex = currentPage * imagesPerRow
  const endIndex = startIndex + imagesPerRow
  const currentImages = images.slice(startIndex, endIndex)

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1))
  }

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
  }

  const goToPreviousMobile = () => {
    setMobileIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const goToNextMobile = () => {
    setMobileIndex((prev) => (prev + 1) % images.length)
  }

  // Grid class based on images per row
  const gridClassMap: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  }
  const gridClasses = gridClassMap[imagesPerRow] || 'grid-cols-3'

  return (
    <div className="relative h-full w-full">
      {/* Desktop: Grid layout with pagination */}
      <div className="hidden md:flex h-full w-full items-center justify-center">
        <div className={`grid ${gridClasses} gap-1.5 w-full h-full`} style={{ gridAutoRows: '1fr' }}>
          {currentImages.map((img: string, idx: number) => (
            <div
              key={startIndex + idx}
              className="relative rounded-lg overflow-hidden shadow-sm flex items-center justify-center"
            >
              <img
                src={img}
                alt=""
                className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Desktop navigation arrows and page counter */}
        {totalPages > 1 && (
          <>
            {currentPage > 0 && (
              <button
                onClick={goToPreviousPage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 active:scale-95 text-white rounded-full flex items-center justify-center transition-all shadow-lg z-10"
                aria-label="Previous page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {currentPage < totalPages - 1 && (
              <button
                onClick={goToNextPage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 active:scale-95 text-white rounded-full flex items-center justify-center transition-all shadow-lg z-10"
                aria-label="Next page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Page counter */}
            <div className="absolute bottom-2 right-2 px-2.5 py-1 bg-black/70 text-white text-xs font-medium rounded-full backdrop-blur-sm">
              Page {currentPage + 1} / {totalPages}
            </div>
          </>
        )}
      </div>

      {/* Mobile: Single image carousel */}
      <div className="flex md:hidden h-full w-full items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={images[mobileIndex]}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
            loading="lazy"
          />

          {/* Mobile navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPreviousMobile}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 active:scale-95 text-white rounded-full flex items-center justify-center transition-all shadow-lg z-10 touch-manipulation"
                aria-label="Previous image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNextMobile}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 active:scale-95 text-white rounded-full flex items-center justify-center transition-all shadow-lg z-10 touch-manipulation"
                aria-label="Next image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Image counter */}
              <div className="absolute bottom-2 right-2 px-2.5 py-1 bg-black/70 text-white text-xs font-medium rounded-full backdrop-blur-sm">
                {mobileIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// Q&A Block
function QnABlock({ block }: BlockProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(block.user_voted_option || null)
  const [isVoting, setIsVoting] = useState(false)
  const [options, setOptions] = useState(block.options || [])

  const handleVote = async (optionId: string) => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      alert('Please sign in to vote')
      return
    }

    try {
      setIsVoting(true)

      const response = await fetch(`${API_URL}/api/newsroom/blocks/${block.id}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ optionId })
      })

      if (!response.ok) {
        throw new Error('Failed to vote')
      }

      const result = await response.json()
      setOptions(result.data)
      setSelectedOption(optionId)
    } catch (error) {
      console.error('Vote error:', error)
    } finally {
      setIsVoting(false)
    }
  }

  const totalVotes = options.reduce((sum: number, opt: any) => sum + (opt.vote_count || 0), 0)

  return (
    <div className="bg-gradient-to-br from-[#F0F8FF] to-[#D4EEFF] rounded-lg p-4 sm:p-5">
      {/* Question */}
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 leading-snug">
        {block.content.question}
      </h3>

      {/* Options */}
      <div className="space-y-2 sm:space-y-2.5">
        {options.map((option: any) => {
          const percentage = totalVotes > 0 ? (option.vote_count / totalVotes) * 100 : 0
          const isSelected = selectedOption === option.id

          return (
            <button
              key={option.id}
              onClick={() => !selectedOption && handleVote(option.id)}
              disabled={isVoting || !!selectedOption}
              className={`w-full text-left px-4 py-3.5 sm:py-4 rounded-lg transition-all duration-200 relative overflow-hidden touch-manipulation min-h-[48px] ${
                isSelected
                  ? 'bg-[#D4EEFF] border-2 border-[#5BB4D6] shadow-md'
                  : selectedOption
                  ? 'bg-white border border-gray-200'
                  : 'bg-white border border-gray-200 hover:border-[#87CEEB] hover:bg-[#F0F8FF] active:scale-[0.98] shadow-sm'
              }`}
            >
              {/* Progress bar */}
              {selectedOption && (
                <div
                  className="absolute inset-0 bg-[#D4EEFF] transition-all"
                  style={{ width: `${percentage}%` }}
                />
              )}

              {/* Content */}
              <div className="relative flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 sm:gap-2.5 flex-1 min-w-0">
                  {isSelected && <CheckCircle size={18} className="text-[#4AA3CC] flex-shrink-0" />}
                  <span className="text-sm sm:text-base font-medium text-gray-900 break-words">{option.option_text}</span>
                </div>
                {selectedOption && (
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-0.5 sm:gap-1.5 flex-shrink-0">
                    <span className="text-base sm:text-lg font-bold text-[#4AA3CC]">
                      {Math.round(percentage)}%
                    </span>
                    <span className="text-xs text-gray-500">
                      ({option.vote_count})
                    </span>
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Metadata */}
      {(totalVotes > 0 || block.content.deadline) && (
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          {totalVotes > 0 && <span>{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>}
          {block.content.deadline && <span>{block.content.deadline}</span>}
        </div>
      )}
    </div>
  )
}

// Link Block - Responsive with better touch targets
function LinkBlock({ block }: BlockProps) {
  return (
    <a
      href={block.content.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-gradient-to-br from-[#F0F8FF] to-sky-50 hover:from-[#D4EEFF] hover:to-sky-100 rounded-lg p-4 sm:p-5 transition-all duration-200 border border-[#B8E2F8] hover:border-[#87CEEB] hover:shadow-md active:scale-[0.98] touch-manipulation min-h-[60px]"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          {block.content.title && (
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1 line-clamp-2">
              {block.content.title}
            </h3>
          )}
          <p className="text-xs sm:text-sm text-[#3B8DB3] truncate font-medium">
            {block.content.url}
          </p>
        </div>
        <ExternalLink size={20} className="flex-shrink-0 text-[#4AA3CC]" />
      </div>
    </a>
  )
}

// Map Pin Block
function MapPinBlock({ block }: BlockProps) {
  const hasCoordinates = block.content.lat && block.content.lng

  // Generate map embed URL using OpenStreetMap (no API key needed!)
  const getMapEmbedUrl = (): string | undefined => {
    if (!hasCoordinates) return undefined
    const lat = parseFloat(block.content.lat)
    const lng = parseFloat(block.content.lng)
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.01},${lng+0.01},${lat+0.01}&layer=mapnik&marker=${lat},${lng}`
  }

  return (
    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg overflow-hidden border border-red-200 shadow-sm">
      {/* Map Display - Only show if coordinates are provided */}
      {hasCoordinates ? (
        <>
          <div className="relative">
            <iframe
              src={getMapEmbedUrl()}
              className="w-full h-48 sm:h-64 md:h-72 border-0"
              title={`Map of ${block.content.location}`}
              loading="lazy"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 sm:p-4">
              <div className="flex items-center gap-2 text-white">
                <MapPin size={18} className="fill-current flex-shrink-0" />
                <span className="font-semibold text-sm sm:text-base">{block.content.location}</span>
              </div>
            </div>
          </div>
          {/* Link to full map */}
          <div className="p-3 sm:p-4 border-t border-red-200 bg-white">
            <a
              href={`https://www.openstreetmap.org/?mlat=${block.content.lat}&mlon=${block.content.lng}#map=15/${block.content.lat}/${block.content.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1.5 transition-colors touch-manipulation"
            >
              <span>View full map</span>
              <ExternalLink size={16} />
            </a>
          </div>
        </>
      ) : (
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <MapPin size={20} className="text-red-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">{block.content.location}</h3>
              <p className="text-sm text-gray-600">Location text only (no map coordinates)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Utility function to format numbers (exported for future use)
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}
